import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { size } from '../../layout/helpers';

export const SidebarContainer = styled.aside<{ $impersonate?: boolean }>`
  --logo-height: ${(props) => props.theme.size.logoHeight};

  background-color: ${(props) => props.theme.color.bg.alt};
  width: ${(props) => props.theme.size.sideBarWidth};
  position: fixed;
  top: ${(props) => (props.$impersonate ? props.theme.size.impersonateBarHeight : '0')};
  left: 0;
  bottom: 0;
  z-index: ${(props) => props.theme.zindex.sidebar.container};
  display: flex;
  flex-direction: column;
  padding: 0;

  ${(props) => props.theme.breakpoint.LtSm} {
    transform: translateY(-100%);
    transition: transform 0.3s ease-in-out;
    width: 100%;
    height: ${(props) =>
      props.$impersonate
        ? `calc(100vh - ${props.theme.size.mobileMenuHeaderHeight} - ${props.theme.size.impersonateBarHeightMobile})`
        : `calc(100vh - ${props.theme.size.mobileMenuHeaderHeight})`};
    top: ${(props) =>
      props.$impersonate
        ? `calc(${props.theme.size.mobileMenuHeaderHeight} + ${props.theme.size.impersonateBarHeightMobile})`
        : props.theme.size.mobileMenuHeaderHeight};
    overflow-y: auto;

    &.mobile-open {
      transform: translateY(0);
    }
  }
`;

export const MobileHeader = styled.div<{ $impersonate?: boolean }>`
  display: none;
  position: fixed;
  top: ${(props) => (props.$impersonate ? props.theme.size.impersonateBarHeightMobile : '0')};
  left: 0;
  right: 0;
  background-color: ${(props) => props.theme.color.bg.alt};
  height: ${(props) => props.theme.size.mobileMenuHeaderHeight};
  z-index: ${(props) => props.theme.zindex.sidebar.mobileHeader};
  padding: 1rem;

  ${(props) => props.theme.breakpoint.LtSm} {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`;

export const MobileLogo = styled.div`
  display: none;
  height: 40px;

  ${(props) => props.theme.breakpoint.LtSm} {
    display: block;
  }

  img {
    height: 100%;
    width: auto;
  }
`;

export const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: ${(props) => props.theme.color.text.main};
  margin-left: auto;

  ${(props) => props.theme.breakpoint.LtSm} {
    display: block;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
  z-index: ${(props) => props.theme.zindex.sidebar.nav};
`;

export const StickyHeader = styled.div`
  padding-left: 24px;
  height: calc(var(--logo-height) + 1.5rem * 2);
  display: flex;
  align-items: center;

  ${(props) => props.theme.breakpoint.LtSm} {
    display: none;
  }
`;

export const Logo = styled.div`
  height: 47px;
`;

export const LogoImg = styled.img`
  display: block;
  height: 100%;
`;

export const InnerNav = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: ${size(2)};

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

export const NavGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const GroupTitle = styled.h4`
  display: flex;
  align-items: flex-start;
  font-size: ${(props) => props.theme.font.size.large};
  font-weight: ${(props) => props.theme.font.weight.bold};
  color: ${(props) => props.theme.color.text.main};
  letter-spacing: -0.2px;
  padding-left: 24px;
  margin-bottom: 4px;
`;

export const NavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 2px;
`;

export const NavItem = styled.li`
  margin: 0 12px;
`;

interface NavItemLinkProps {
  $isActive?: boolean;
  end?: boolean;
}

const iconBaseStyles = css`
  display: flex;
  align-items: center;
  width: 24px;
  height: 24px;
  color: ${(props) => props.theme.color.text.alt};
`;

const iconHoverStyles = css`
  color: ${(props) => props.theme.color.text.main};
`;

const navItemBaseStyles = css<NavItemLinkProps>`
  height: 30px;
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  border-radius: 6px;
  padding: 0 10px;
  text-decoration: none;
  width: 100%;
  color: ${(props) => props.theme.color.text.alt};
  transition: color 0.3s ease-in-out;
  gap: ${size(1.25)};

  &:hover {
    background-color: ${(props) => props.theme.color.bg.main};
    color: ${(props) => props.theme.color.text.main};

    & > div {
      ${iconHoverStyles}
    }
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      background-color: ${(props) => props.theme.color.bg.main};
      color: ${(props) => props.theme.color.text.main};
      font-weight: ${(props) => props.theme.font.weight.bold};

      & > div {
        ${iconHoverStyles}
      }
    `}
`;

export const NavItemLink = styled(NavLink).attrs<NavItemLinkProps>(({ end }) => ({
  end,
}))`
  ${navItemBaseStyles}
`;

export const NavItemLinkButton = styled.a<NavItemLinkProps>`
  ${navItemBaseStyles}
`;

export const NavItemIcon = styled.div`
  ${iconBaseStyles}
`;

export const SidebarProfileWrapper = styled.div`
  margin: 24px 0 16px 0;
  padding: 0 16px;
`;
